import { useRef, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import { feedbackService, type FeedbackCategory } from '@/services/feedbackService'

interface FeedbackDialogProps {
  open: boolean
  onClose: () => void
}

const MAX_IMAGES = 6
const MAX_IMAGE_BYTES = 10 * 1024 * 1024 // 10 MB

export default function FeedbackDialog({ open, onClose }: FeedbackDialogProps) {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')
  const { t: tCommon } = useTranslation('common')

  const [category, setCategory] = useState<FeedbackCategory>('issue')
  const [message, setMessage] = useState('')
  const [images, setImages] = useState<File[]>([])
  const [dragging, setDragging] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const reset = () => {
    setCategory('issue')
    setMessage('')
    setImages([])
    setDragging(false)
    setError(null)
    setSuccess(false)
  }

  const handleClose = () => {
    if (loading) return
    reset()
    onClose()
  }

  const addFiles = (files: FileList | File[]) => {
    setError(null)
    const incoming = Array.from(files).filter((f) => f.type.startsWith('image/'))
    const tooBig = incoming.find((f) => f.size > MAX_IMAGE_BYTES)
    if (tooBig) {
      setError(t('feedback.imageTooLarge'))
      return
    }
    setImages((prev) => [...prev, ...incoming].slice(0, MAX_IMAGES))
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files)
  }

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async () => {
    if (!message.trim()) return
    try {
      setLoading(true)
      setError(null)
      await feedbackService.submit({ category, message: message.trim(), images })
      setSuccess(true)
      setTimeout(handleClose, 1800)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('feedback.submitFailed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {t('feedback.title')}
        <IconButton onClick={handleClose} disabled={loading} size="small" sx={{ color: c.ink48 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ my: 1 }}>
            {t('feedback.success')}
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('feedback.subtitle')}
            </Typography>

            <ToggleButtonGroup
              value={category}
              exclusive
              fullWidth
              onChange={(_, val) => val && setCategory(val as FeedbackCategory)}
              disabled={loading}
              size="small"
            >
              <ToggleButton value="issue" sx={{ gap: 1, textTransform: 'none' }}>
                <BugReportOutlinedIcon sx={{ fontSize: 18 }} />
                {t('feedback.issue')}
              </ToggleButton>
              <ToggleButton value="feature" sx={{ gap: 1, textTransform: 'none' }}>
                <LightbulbOutlinedIcon sx={{ fontSize: 18 }} />
                {t('feedback.feature')}
              </ToggleButton>
            </ToggleButtonGroup>

            <TextField
              autoFocus
              label={t('feedback.messageLabel')}
              placeholder={
                category === 'issue'
                  ? t('feedback.issuePlaceholder')
                  : t('feedback.featurePlaceholder')
              }
              multiline
              minRows={4}
              fullWidth
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={loading}
            />

            {/* Drag-and-drop image dropzone */}
            <Box
              onClick={() => !loading && fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault()
                setDragging(true)
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              sx={{
                border: `1.5px dashed ${dragging ? c.coral : c.ink32}`,
                bgcolor: dragging ? c.coralSoft : c.gray50,
                borderRadius: 2,
                p: 2.5,
                textAlign: 'center',
                cursor: loading ? 'default' : 'pointer',
                transition: 'border-color .15s, background-color .15s',
              }}
            >
              <CloudUploadOutlinedIcon sx={{ fontSize: 24, color: c.ink48, mb: 0.5 }} />
              <Typography variant="body2" color="text.secondary">
                {t('feedback.dropzone')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('feedback.dropzoneHint', { max: MAX_IMAGES })}
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => {
                  if (e.target.files?.length) addFiles(e.target.files)
                  e.target.value = ''
                }}
              />
            </Box>

            {images.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {images.map((file, i) => (
                  <Box
                    key={i}
                    sx={{
                      position: 'relative',
                      width: 72,
                      height: 72,
                      borderRadius: 1.5,
                      overflow: 'hidden',
                      border: `1px solid ${c.ink08}`,
                    }}
                  >
                    <Box
                      component="img"
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removeImage(i)}
                      disabled={loading}
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        bgcolor: 'rgba(0,0,0,0.55)',
                        color: '#fff',
                        p: 0.25,
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      {!success && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            {tCommon('cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!message.trim() || loading}
            startIcon={loading ? <CircularProgress size={18} /> : null}
          >
            {t('feedback.submit')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}
